import React, { useState, useEffect } from 'react';
import { DataGrid } from '../components/common/DataGrid';
import { FilterBar } from '../components/common/FilterBar';
import { SupplierAuthEditor } from '../components/supplier/SupplierAuthEditor';
import { supplierManagementApi } from '../api/supplierManagementApi';
import { formatDateTime } from '../utils/formatDateTime';

const SupplierManagementPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ keyword: '', isActive: null });
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [action, setAction] = useState('create'); // 'create', 'password'

  useEffect(() => {
    loadItems();
  }, [filter]);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await supplierManagementApi.getSupplierAuthList(
        filter.keyword || undefined,
        filter.isActive !== null ? filter.isActive : undefined
      );
      setItems(data);
    } catch (error) {
      console.error('一覧取得エラー:', error);
      alert('一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { header: '仕入先CD', field: 'supplier_code' },
    { header: '仕入先名', field: 'supplier_name' },
    { header: 'ステータス', field: 'is_active', render: (row) => row.is_active ? '有効' : '無効' },
    { header: '作成日時', field: 'created_at', render: (row) => formatDateTime(row.created_at) },
    { header: '更新日時', field: 'updated_at', render: (row) => formatDateTime(row.updated_at) },
    { header: '作成者', field: 'created_by', render: (row) => row.created_by || '-' },
    { header: '更新者', field: 'updated_by', render: (row) => row.updated_by || '-' },
  ];

  const handleCreate = () => {
    setEditingItem(null);
    setAction('create');
    setEditorOpen(true);
  };

  const handlePasswordChange = (row) => {
    setEditingItem(row);
    setAction('password');
    setEditorOpen(true);
  };

  const handleStatusChange = async (row) => {
    if (!window.confirm(`${row.is_active ? '無効化' : '有効化'}しますか？`)) return;
    
    try {
      await supplierManagementApi.updateStatus(row.supplier_code, !row.is_active);
      await loadItems();
    } catch (error) {
      alert(error.message || 'ステータス変更に失敗しました');
    }
  };

  const handleSave = async (data) => {
    try {
      if (action === 'create') {
        const response = await supplierManagementApi.createSupplierAuth(data.supplier_code, data.password);
        alert(`作成しました。初期パスワード: ${response.password}`);
      } else if (action === 'password') {
        const response = await supplierManagementApi.updatePassword(
          editingItem.supplier_code,
          data.mode,
          data.new_password
        );
        if (response.mode === 'temp' && response.password) {
          window.prompt(
            '自動生成パスワードを設定しました。コピーして控えてください（再表示不可）',
            response.password
          );
        } else {
          alert('パスワードを変更しました。\nこのパスワードは再表示できません。');
        }
      }
      await loadItems();
      setEditorOpen(false);
      setEditingItem(null);
    } catch (error) {
      throw error;
    }
  };

  return (
    <div className="h-full flex flex-col bg-slate-50">
      <div className="p-6 border-b border-slate-200 bg-white compact:px-4 compact:py-3">
        <h1 className="page-title">仕入先認証管理</h1>
      </div>
      <div className="px-6 py-3 border-b border-slate-200 bg-white compact:px-4 compact:py-2 page-header">
        <FilterBar 
          onFilterChange={(newFilter) => setFilter(prev => ({ ...prev, ...newFilter }))}
          additionalFilters={
            <select
              value={filter.isActive === null ? '' : filter.isActive ? 'true' : 'false'}
              onChange={(e) => setFilter(prev => ({ 
                ...prev, 
                isActive: e.target.value === '' ? null : e.target.value === 'true' 
              }))}
              className="px-3 py-1 border border-slate-300 rounded-md text-sm"
            >
              <option value="">すべて</option>
              <option value="true">有効</option>
              <option value="false">無効</option>
            </select>
          }
        />
        <button
          onClick={handleCreate}
          className="btn-toolbar bg-slate-700 text-white rounded-md hover:bg-slate-800 text-sm font-medium shrink-0"
        >
          新規作成
        </button>
      </div>
      <div className="flex-1 overflow-auto p-6">
        <DataGrid
          columns={columns}
          data={items}
          loading={loading}
          actionButtons={(row) => (
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePasswordChange(row);
                }}
                className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
              >
                パスワード変更
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusChange(row);
                }}
                className={`px-3 py-1 rounded text-xs ${
                  row.is_active 
                    ? 'bg-red-600 text-white hover:bg-red-700' 
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {row.is_active ? '無効化' : '有効化'}
              </button>
            </div>
          )}
        />
      </div>
      <SupplierAuthEditor
        isOpen={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setEditingItem(null);
        }}
        item={editingItem}
        action={action}
        onSave={handleSave}
      />
    </div>
  );
};

export default SupplierManagementPage;
